<template>
  <div class="dashboard-page">
    <!-- License status banners (only shown for grace/inactive states) -->
    <div
      v-if="licenseStatus?.status === 'grace'"
      class="license-banner license-banner-warning"
    >
      <span class="license-banner-icon">⚠</span>
      <span>
        {{ licenseStatus.grace_days_remaining === 1
          ? $t('admin.dashboard.license_grace_banner', { n: licenseStatus.grace_days_remaining })
          : $t('admin.dashboard.license_grace_banner_plural', { n: licenseStatus.grace_days_remaining }) }}
      </span>
      <router-link to="/admin/settings" class="license-banner-link">{{ $t('admin.dashboard.go_to_settings') }}</router-link>
    </div>

    <div
      v-else-if="licenseStatus && !['active', 'grace'].includes(licenseStatus.status)"
      class="license-banner license-banner-error"
    >
      <span class="license-banner-icon">✕</span>
      <span>{{ $t('admin.dashboard.license_inactive_banner') }}</span>
      <router-link to="/admin/settings" class="license-banner-link">{{ $t('admin.dashboard.go_to_settings') }}</router-link>
    </div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.dashboard.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.dashboard.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <select v-model="period" class="period-select" @change="fetchCharts">
          <option value="7d">{{ $t('admin.dashboard.period_7d') }}</option>
          <option value="30d">{{ $t('admin.dashboard.period_30d') }}</option>
          <option value="90d">{{ $t('admin.dashboard.period_90d') }}</option>
        </select>
        <button class="btn btn-secondary" @click="refreshAll">{{ $t('admin.dashboard.refresh') }}</button>
      </div>
    </div>

    <!-- KPI Stat Cards -->
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-revenue" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '\u2014' : formatCents(stats.revenue_today) }}</div>
        <div class="stat-label">{{ $t('admin.dashboard.stat_revenue_today') }}</div>
      </div>

      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-players" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '\u2014' : stats.active_players_today.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.dashboard.stat_active_players') }}</div>
      </div>

      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-deposits" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '\u2014' : formatCents(stats.total_deposits_today) }}</div>
        <div class="stat-label">{{ $t('admin.dashboard.stat_deposits_today') }}</div>
      </div>

      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-withdrawals" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '\u2014' : formatCents(stats.total_withdrawals_today) }}</div>
        <div class="stat-label">{{ $t('admin.dashboard.stat_withdrawals_today') }}</div>
      </div>

      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-ggr" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '\u2014' : formatCents(stats.ggr_today) }}</div>
        <div class="stat-label">{{ $t('admin.dashboard.stat_ggr_today') }}</div>
      </div>

      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-pending" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '\u2014' : stats.pending_withdrawals_count.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.dashboard.stat_pending_withdrawals') }}</div>
        <div class="stat-sub">{{ statsLoading ? '' : formatCents(stats.pending_withdrawals_amount) }}</div>
      </div>

      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-new-users" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '\u2014' : stats.new_registrations_today.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.dashboard.stat_new_registrations') }}</div>
      </div>
    </div>

    <!-- Charts -->
    <div class="chart-grid">
      <div class="chart-card">
        <div class="chart-header">
          <div class="chart-title">{{ $t('admin.dashboard.chart_revenue') }}</div>
          <div class="chart-period">{{ periodLabel }}</div>
        </div>
        <div class="chart-body">
          <canvas ref="revenueChartEl"></canvas>
        </div>
      </div>

      <div class="chart-card">
        <div class="chart-header">
          <div class="chart-title">{{ $t('admin.dashboard.chart_top_games') }}</div>
          <div class="chart-period">{{ periodLabel }}</div>
        </div>
        <div class="chart-body">
          <canvas ref="topGamesChartEl"></canvas>
        </div>
      </div>

      <div class="chart-card">
        <div class="chart-header">
          <div class="chart-title">{{ $t('admin.dashboard.chart_registrations') }}</div>
          <div class="chart-period">{{ periodLabel }}</div>
        </div>
        <div class="chart-body">
          <canvas ref="registrationsChartEl"></canvas>
        </div>
      </div>

      <div class="chart-card">
        <div class="chart-header">
          <div class="chart-title">{{ $t('admin.dashboard.chart_deposits_withdrawals') }}</div>
          <div class="chart-period">{{ periodLabel }}</div>
        </div>
        <div class="chart-body">
          <canvas ref="depositsWithdrawalsChartEl"></canvas>
        </div>
      </div>
    </div>

    <!-- Recent Activity Feed -->
    <div class="activity-section">
      <div class="activity-header">
        <div class="activity-title">{{ $t('admin.dashboard.activity_title') }}</div>
      </div>

      <div v-if="activityLoading" class="activity-loading">
        <span class="spinner"></span>
        {{ $t('admin.dashboard.activity_loading') }}
      </div>

      <template v-else>
        <div v-if="recentActivity.length === 0" class="activity-empty">
          {{ $t('admin.dashboard.activity_empty') }}
        </div>

        <div
          v-for="(event, idx) in recentActivity"
          v-else
          :key="idx"
          class="activity-item"
        >
          <div class="activity-icon" :class="activityIconClass(event.type)">
            <AppIcon :name="activityIcon(event.type)" :size="14" />
          </div>
          <div class="activity-content">
            <div class="activity-text">
              <strong>{{ activityLabel(event.type) }}:</strong>
              {{ event.player }}
              <template v-if="event.amount !== null">
                &mdash; {{ formatCents(event.amount) }}
              </template>
            </div>
            <div class="activity-time">{{ formatTimeAgo(event.created_at) }}</div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'
import { useAdminStore } from '@/stores/admin'

const { t } = useI18n()
import {
  Chart,
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'

Chart.register(
  LineController,
  BarController,
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
)

const toast = useToastStore()
const adminStore = useAdminStore()
const licenseStatus = computed(() => adminStore.licenseStatus)

// ----------- State -----------

interface DashboardStats {
  revenue_today: number
  revenue_week: number
  revenue_month: number
  active_players_now: number
  active_players_today: number
  active_players_month: number
  total_deposits_today: number
  total_withdrawals_today: number
  ggr_today: number
  ngr_today: number
  new_registrations_today: number
  pending_withdrawals_count: number
  pending_withdrawals_amount: number
}

interface RevenueDay {
  date: string
  amount: number
}

interface RegistrationDay {
  date: string
  count: number
}

interface TopGame {
  slug: string
  name: string
  ggr: number
}

interface DepositWithdrawalDay {
  date: string
  deposits: number
  withdrawals: number
}

interface ChartsData {
  revenue_by_day: RevenueDay[]
  registrations_by_day: RegistrationDay[]
  top_games: TopGame[]
  deposits_vs_withdrawals: DepositWithdrawalDay[]
}

interface ActivityEvent {
  type: string
  message: string
  amount: number | null
  player: string
  created_at: string
}

const emptyStats: DashboardStats = {
  revenue_today: 0,
  revenue_week: 0,
  revenue_month: 0,
  active_players_now: 0,
  active_players_today: 0,
  active_players_month: 0,
  total_deposits_today: 0,
  total_withdrawals_today: 0,
  ggr_today: 0,
  ngr_today: 0,
  new_registrations_today: 0,
  pending_withdrawals_count: 0,
  pending_withdrawals_amount: 0,
}

const stats = ref<DashboardStats>({ ...emptyStats })
const statsLoading = ref(true)
const recentActivity = ref<ActivityEvent[]>([])
const activityLoading = ref(true)
const period = ref('30d')

// Chart refs
const revenueChartEl = ref<HTMLCanvasElement | null>(null)
const topGamesChartEl = ref<HTMLCanvasElement | null>(null)
const registrationsChartEl = ref<HTMLCanvasElement | null>(null)
const depositsWithdrawalsChartEl = ref<HTMLCanvasElement | null>(null)

let revenueChart: Chart | null = null
let topGamesChart: Chart | null = null
let registrationsChart: Chart | null = null
let depositsWithdrawalsChart: Chart | null = null

let refreshTimer: ReturnType<typeof setInterval> | null = null

// ----------- Computed -----------

const periodLabel = computed(() => {
  switch (period.value) {
    case '7d':
      return t('admin.dashboard.period_label_7d')
    case '90d':
      return t('admin.dashboard.period_label_90d')
    default:
      return t('admin.dashboard.period_label_30d')
  }
})

// ----------- Formatting -----------

function formatCents(cents: number): string {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatTimeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return t('admin.dashboard.time_just_now')
  if (minutes < 60) return t('admin.dashboard.time_minutes_ago', { m: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('admin.dashboard.time_hours_ago', { h: hours })
  const days = Math.floor(hours / 24)
  return t('admin.dashboard.time_days_ago', { d: days })
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function activityIconClass(type: string): string {
  switch (type) {
    case 'registration':
      return 'icon-user'
    case 'large_deposit':
      return 'icon-deposit'
    case 'large_win':
      return 'icon-win'
    case 'withdrawal_request':
      return 'icon-withdraw'
    default:
      return 'icon-user'
  }
}

function activityIcon(type: string): string {
  const map: Record<string, string> = {
    registration: 'activity-registration',
    large_deposit: 'activity-large-deposit',
    large_win: 'activity-large-win',
    withdrawal_request: 'activity-withdrawal-request',
  }
  return map[type] ?? 'activity-registration'
}

function activityLabel(type: string): string {
  switch (type) {
    case 'registration':
      return t('admin.dashboard.activity_registration')
    case 'large_deposit':
      return t('admin.dashboard.activity_large_deposit')
    case 'large_win':
      return t('admin.dashboard.activity_large_win')
    case 'withdrawal_request':
      return t('admin.dashboard.activity_withdrawal_request')
    default:
      return t('admin.dashboard.activity_event')
  }
}

// ----------- Chart helpers -----------

const chartColors = {
  green: '#00E701',
  greenFill: 'rgba(0, 231, 1, 0.1)',
  blue: '#1475E1',
  blueFill: 'rgba(20, 117, 225, 0.1)',
  gold: '#FFD700',
  orange: '#FFA726',
  orangeFill: 'rgba(255, 167, 38, 0.1)',
  gridColor: 'rgba(255, 255, 255, 0.06)',
  tickColor: '#7F8FA4',
}

function baseChartOptions(yTickCallback?: (value: number | string) => string): object {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400 },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1A2C38',
        titleColor: '#fff',
        bodyColor: '#B1BAD3',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
      },
    },
    scales: {
      x: {
        grid: { color: chartColors.gridColor },
        ticks: { color: chartColors.tickColor, font: { size: 11 }, maxRotation: 0 },
      },
      y: {
        grid: { color: chartColors.gridColor },
        ticks: {
          color: chartColors.tickColor,
          font: { size: 11 },
          ...(yTickCallback ? { callback: yTickCallback } : {}),
        },
        beginAtZero: true,
      },
    },
  }
}

function centsTickCallback(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (num >= 100000) return '$' + (num / 100000).toFixed(0) + 'k'
  if (num >= 100) return '$' + (num / 100).toFixed(0)
  return '$' + (num / 100).toFixed(2)
}

function destroyCharts(): void {
  revenueChart?.destroy()
  topGamesChart?.destroy()
  registrationsChart?.destroy()
  depositsWithdrawalsChart?.destroy()
  revenueChart = null
  topGamesChart = null
  registrationsChart = null
  depositsWithdrawalsChart = null
}

function renderCharts(data: ChartsData): void {
  destroyCharts()

  // Revenue line chart
  if (revenueChartEl.value) {
    revenueChart = new Chart(revenueChartEl.value, {
      type: 'line',
      data: {
        labels: data.revenue_by_day.map((d) => formatShortDate(d.date)),
        datasets: [
          {
            label: t('admin.dashboard.chart_dataset_revenue'),
            data: data.revenue_by_day.map((d) => d.amount),
            borderColor: chartColors.green,
            backgroundColor: chartColors.greenFill,
            fill: true,
            tension: 0.3,
            pointRadius: 2,
            pointHoverRadius: 5,
            borderWidth: 2,
          },
        ],
      },
      options: {
        ...baseChartOptions(centsTickCallback),
        plugins: {
          ...(baseChartOptions(centsTickCallback) as { plugins: object }).plugins,
          tooltip: {
            ...(baseChartOptions(centsTickCallback) as { plugins: { tooltip: object } }).plugins
              .tooltip,
            callbacks: {
              label: (ctx: { parsed: { y: number } }) => formatCents(ctx.parsed.y),
            },
          },
        },
      } as object,
    })
  }

  // Top games horizontal bar chart
  if (topGamesChartEl.value) {
    topGamesChart = new Chart(topGamesChartEl.value, {
      type: 'bar',
      data: {
        labels: data.top_games.map((g) => g.name),
        datasets: [
          {
            label: t('admin.dashboard.chart_dataset_ggr'),
            data: data.top_games.map((g) => g.ggr),
            backgroundColor: chartColors.blue,
            borderRadius: 4,
            barPercentage: 0.7,
          },
        ],
      },
      options: {
        ...(baseChartOptions(centsTickCallback) as object),
        indexAxis: 'y' as const,
        plugins: {
          ...(baseChartOptions(centsTickCallback) as { plugins: object }).plugins,
          tooltip: {
            ...(baseChartOptions(centsTickCallback) as { plugins: { tooltip: object } }).plugins
              .tooltip,
            callbacks: {
              label: (ctx: { parsed: { x: number } }) => formatCents(ctx.parsed.x),
            },
          },
        },
      } as object,
    })
  }

  // Registrations bar chart
  if (registrationsChartEl.value) {
    registrationsChart = new Chart(registrationsChartEl.value, {
      type: 'bar',
      data: {
        labels: data.registrations_by_day.map((d) => formatShortDate(d.date)),
        datasets: [
          {
            label: t('admin.dashboard.chart_dataset_registrations'),
            data: data.registrations_by_day.map((d) => d.count),
            backgroundColor: chartColors.green,
            borderRadius: 4,
            barPercentage: 0.6,
          },
        ],
      },
      options: baseChartOptions() as object,
    })
  }

  // Deposits vs Withdrawals line chart
  if (depositsWithdrawalsChartEl.value) {
    depositsWithdrawalsChart = new Chart(depositsWithdrawalsChartEl.value, {
      type: 'line',
      data: {
        labels: data.deposits_vs_withdrawals.map((d) => formatShortDate(d.date)),
        datasets: [
          {
            label: t('admin.dashboard.chart_dataset_deposits'),
            data: data.deposits_vs_withdrawals.map((d) => d.deposits),
            borderColor: chartColors.green,
            backgroundColor: chartColors.greenFill,
            fill: true,
            tension: 0.3,
            pointRadius: 2,
            pointHoverRadius: 5,
            borderWidth: 2,
          },
          {
            label: t('admin.dashboard.chart_dataset_withdrawals'),
            data: data.deposits_vs_withdrawals.map((d) => d.withdrawals),
            borderColor: chartColors.orange,
            backgroundColor: chartColors.orangeFill,
            fill: true,
            tension: 0.3,
            pointRadius: 2,
            pointHoverRadius: 5,
            borderWidth: 2,
          },
        ],
      },
      options: {
        ...baseChartOptions(centsTickCallback),
        plugins: {
          ...(baseChartOptions(centsTickCallback) as { plugins: object }).plugins,
          legend: {
            display: true,
            position: 'top' as const,
            labels: {
              color: chartColors.tickColor,
              font: { size: 11 },
              boxWidth: 12,
              padding: 16,
            },
          },
          tooltip: {
            ...(baseChartOptions(centsTickCallback) as { plugins: { tooltip: object } }).plugins
              .tooltip,
            callbacks: {
              label: (ctx: { dataset: { label?: string }; parsed: { y: number } }) =>
                (ctx.dataset.label ?? '') + ': ' + formatCents(ctx.parsed.y),
            },
          },
        },
      } as object,
    })
  }
}

// ----------- Data fetching -----------

async function fetchStats(): Promise<void> {
  statsLoading.value = true
  try {
    const response = await apiClient.get('/admin/api/dashboard/stats')
    stats.value = response.data as DashboardStats
  } catch {
    toast.error(t('admin.dashboard.error_stats'))
  } finally {
    statsLoading.value = false
  }
}

async function fetchCharts(): Promise<void> {
  try {
    const response = await apiClient.get('/admin/api/dashboard/charts', {
      params: { period: period.value },
    })
    await nextTick()
    renderCharts(response.data as ChartsData)
  } catch {
    toast.error(t('admin.dashboard.error_charts'))
  }
}

async function fetchActivity(): Promise<void> {
  activityLoading.value = true
  try {
    const response = await apiClient.get('/admin/api/dashboard/recent-activity')
    recentActivity.value = response.data as ActivityEvent[]
  } catch {
    toast.error(t('admin.dashboard.error_activity'))
  } finally {
    activityLoading.value = false
  }
}

async function refreshAll(): Promise<void> {
  await Promise.all([fetchStats(), fetchCharts(), fetchActivity()])
}

// ----------- Lifecycle -----------

onMounted(async () => {
  await refreshAll()
  // Auto-refresh every 60 seconds
  refreshTimer = setInterval(refreshAll, 60000)
})

onBeforeUnmount(() => {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
  destroyCharts()
})
</script>

<style scoped>
.dashboard-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

/* License banners */
.license-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 20px;
  line-height: 1.4;
}

.license-banner-warning {
  background: rgba(255, 167, 38, 0.12);
  border: 1px solid rgba(255, 167, 38, 0.3);
  color: #ffa726;
}

.license-banner-error {
  background: rgba(229, 57, 53, 0.1);
  border: 1px solid rgba(229, 57, 53, 0.3);
  color: #e53935;
}

.license-banner-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.license-banner span:not(.license-banner-icon) {
  flex: 1;
}

.license-banner-link {
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  color: inherit;
  white-space: nowrap;
  padding: 4px 10px;
  border: 1px solid currentColor;
  border-radius: var(--radius-sm, 4px);
  opacity: 0.85;
  flex-shrink: 0;
}

.license-banner-link:hover {
  opacity: 1;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.period-select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
}

.period-select option {
  background: var(--bg-secondary, #1a2c38);
}

.period-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  background: #2a4455;
  color: var(--text-primary, #fff);
}

/* Stat Cards */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  transition: all 150ms ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-icon {
  font-size: 24px;
  margin-bottom: 12px;
}

.stat-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-sub {
  font-size: 12px;
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-secondary, #b1bad3);
  margin-top: 4px;
}

/* Chart Grid */
.chart-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.chart-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 16px;
  transition: border-color 150ms ease;
}

.chart-card:hover {
  border-color: rgba(255, 255, 255, 0.1);
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.chart-period {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.chart-body {
  width: 100%;
  height: 220px;
  position: relative;
}

/* Activity Section */
.activity-section {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  max-height: 420px;
  overflow-y: auto;
}

.activity-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.activity-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.activity-loading,
.activity-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 20px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.activity-icon.icon-user {
  background: rgba(20, 117, 225, 0.15);
  color: #1475e1;
}

.activity-icon.icon-deposit {
  background: rgba(0, 231, 1, 0.15);
  color: #00e701;
}

.activity-icon.icon-win {
  background: rgba(255, 215, 0, 0.15);
  color: #ffd700;
}

.activity-icon.icon-withdraw {
  background: rgba(255, 167, 38, 0.15);
  color: #ffa726;
}

.activity-content {
  flex: 1;
}

.activity-text {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.4;
}

.activity-text strong {
  color: var(--text-primary, #fff);
  font-weight: 600;
}

.activity-time {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 4px;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 1200px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .chart-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-page {
    padding: 16px;
  }

  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-actions {
    flex-wrap: wrap;
  }
}

/* =========================================================
   RTL overrides
   ========================================================= */
/* Monetary values always render LTR (numbers, currency symbols) */
[dir='rtl'] .stat-value,
[dir='rtl'] .stat-sub {
  direction: ltr;
  unicode-bidi: embed;
}

/* Activity amounts stay LTR */
[dir='rtl'] .activity-time {
  text-align: left;
}
</style>
